import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { LabelWidthValues, ResizeDirection, TemplateState } from '../../../../types/common/types';
import {
  API_TYPE_INTEGRITY_MONITOR_EVENTS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_COMPANY_FILTER,
  WEB_COMPONENT_GZ_SIDE_MENU,
} from '../../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { defaultPageSizeOptions, SelectionStoreMode } from '../../../../grid/gridView/types';
import {
  Column,
  ColumnKeys,
  GridActions,
  IntegrityMonitorEventsGridItem,
  IntegrityMonitorEventsWebComponentSchema,
  PREDEFINED_FILTERS_KEYS,
} from '../types';
import { IntegrityMonitorEventsGridDataProvider } from '../dataProvider/IntegrityMonitorEventsGridDataProvider';
import { dateRenderer } from '@nirvana/shared/common';
import { GzSideMenuWebComponent } from '../../../../ui/gzSideMenu';
import { integrityMonitorChangesRenderer, integrityMonitorEndpointRenderer } from './customGridRenderers';
import { severityRenderer } from '../../../common/customGridRenderers';
import { SideMenuWidthValues } from '../../../../ui/gzSideMenu/types';
import { GzSelectWebComponent } from '../../../../ui/gzSelect';

export class IntegrityMonitorEventsView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('#page-title');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<
      Column,
      ColumnKeys,
      IntegrityMonitorEventsGridItem
    >;
    this.dom.$sideMenu = this.shadow.querySelector(WEB_COMPONENT_GZ_SIDE_MENU) as GzSideMenuWebComponent;
    this.dom.$changeCategorySelect = this.dom.$gridView.querySelector('#changeCategorySelect') as GzSelectWebComponent;
    this.dom.$changeCategoryButtonText = this.dom.$gridView.querySelector('#changeCategoryText');
    this.dom.$companyFilter = this.shadow.querySelector(WEB_COMPONENT_GZ_COMPANY_FILTER);

    this.getTranslations();
    this.setTranslatedStrings();

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getIntegrityMonitorStrings().integrityMonitorEvents;
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$title.innerText = this.translations.gridTitle;
    this.dom.$changeCategoryButtonText.textContent = this.translations.changeCategory;
  }

  async buildSuccessView(data: IntegrityMonitorEventsWebComponentSchema) {
    if (!this.dependencies.canHaveChildCompanies()) {
      if (!this.dom.$companyFilter.classList.contains('hidden')) {
        this.dom.$companyFilter.classList.add('hidden');
      }
    } else {
      this.dom.$companyFilter.classList.remove('hidden');
      await this.buildCompanyFilter();
    }

    if (!this.dependencies.shouldDisplayUpdateCategory()) {
      this.dom.$changeCategorySelect.classList.add('hidden');
    } else {
      this.dom.$changeCategorySelect.classList.remove('hidden');
    }

    this.buildGridView(data);
  }

  private buildGridView(data: IntegrityMonitorEventsWebComponentSchema) {
    const view = data.views[0];

    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.dataProvider = new IntegrityMonitorEventsGridDataProvider(this.dependencies);

    this.dom.$gridView.viewId = view.id;
    this.dom.$gridView.allColumnDetails = data.allColumns;
    this.dom.$gridView.columns = view.displayedColumns;
    this.dom.$gridView.filters = view.filters;
    this.dom.$gridView.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$gridView.isCompactViewChecked = false;
    this.dom.$gridView.defaultViewProps = data.defaultViewProps;
    this.dom.$gridView.pageSize = view.pageSize;
    this.dom.$gridView.pageNumber = view.pageNumber;
    this.dom.$gridView.showGlobalActions = true;
    this.dom.$gridView.hasSelectableRows = this.dependencies.shouldDisplayUpdateCategory();
    this.dom.$gridView.enableCSVExport = false;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.enableRowClickEvent = true;
    this.dom.$gridView.pageSizeOptions = defaultPageSizeOptions;
    this.dom.$gridView.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
    };
    // Allow the user to filter events no older than 1 year
    this.dom.$gridView.filtersUiConfigs = [
      {
        key: ColumnKeys.INTEGRITY_MONITOR_EVENT_DATE,
        config: {
          activeRange: 365,
        },
      },
    ];

    this.dom.$gridView.sideMenuParent = this.shadow;

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.INTEGRITY_MONITOR_EVENT_DATE]: dateRenderer.bind(this),
      [ColumnKeys.INTEGRITY_MONITOR_ENDPOINT]: integrityMonitorEndpointRenderer.bind(this),
      [ColumnKeys.INTEGRITY_MONITOR_ATTRIBUTE_CHANGES]: integrityMonitorChangesRenderer.bind(this),
      [ColumnKeys.INTEGRITY_MONITOR_SEVERITY]: severityRenderer.bind(this),
    };

    this.dom.$gridView.extraParams = {
      companyIds: this.getSelectedCompanyIds(),
    };

    this.dom.$sideMenu.closeOnClickOutside = true;
    this.dom.$sideMenu.menuWidth = SideMenuWidthValues.MEDIUM;
    this.dom.$sideMenu.resizable = ResizeDirection.LEFT;

    this.dom.$gridView.propertiesSet = true;
    this.buildChangeCategorySelect();
  }

  private getSelectedCompanyIds(): Array<string> {
    // Check if "All direct companies" is already selected
    if (this.dom.$companyFilter.specialValueSelected) {
      return [this.dom.$companyFilter.specialValueSelected];
    }

    // Retrieve the companies selection
    const selectedCompanyIds = this.dom.$companyFilter.filterOptions
      .filter(option => option.selected)
      .map(option => option.value);

    // If no selection has been made, return the current user's company
    if (!selectedCompanyIds.length) {
      return [this.dependencies.getCurrentUserCompany().id];
    }

    return selectedCompanyIds;
  }

  private getCategoryOptionIndex(optionValue: GridActions): number {
    return this.dom.$changeCategorySelect.options.findIndex(option => option.value === optionValue);
  }

  public setCategoryOptionStatus(optionValue: GridActions, value: boolean) {
    const optionIndex = this.getCategoryOptionIndex(optionValue);
    if (optionIndex >= 0) {
      this.dom.$changeCategorySelect.options[optionIndex].disabled = !value;
    }
  }

  private async buildCompanyFilter() {
    this.dom.$companyFilter.labelWidth = LabelWidthValues.LARGE;
    this.dom.$companyFilter.noSearchResultsString = this.commonStrings.noResultsFound;
    this.dom.$companyFilter.confirmActionButtonString = this.commonStrings.apply;
    this.dom.$companyFilter.searchPlaceholderString = this.commonStrings.search;
    this.dom.$companyFilter.canSelectAll = true;
    this.dom.$companyFilter.showOnlySelectAllDirectManagement = true;
    this.dom.$companyFilter.allDirectManagementLabel = this.translations.allDirectlyManaged;
    this.dom.$companyFilter.errorMessage = this.commonStrings.genericError;
    this.dom.$companyFilter.overwriteList = true;

    this.dom.$companyFilter.injectDependencies({
      ...this.dependencies,
      ...{ api: { type: API_TYPE_INTEGRITY_MONITOR_EVENTS, clients: this.dependencies.api.clients } },
    });

    const selectedCompanyIds = this.getSelectedCompanyIds();
    const selectedCompanyIdsEntries = selectedCompanyIds.map(companyId => [companyId, true]);
    const objectWithSelectedCompanyIds = Object.fromEntries(selectedCompanyIdsEntries);

    // Force re-fetching of the companies list, in case some companies have been created/edited/deleted
    // since the last time when the company selector has been rendered
    this.dom.$companyFilter.filterOptions = [];

    await this.dom.$companyFilter.controller.setFilterOptions(objectWithSelectedCompanyIds);
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private buildChangeCategorySelect() {
    this.dom.$changeCategorySelect.options = [
      {
        title: this.translations.approveEvent,
        value: GridActions.APPROVE_EVENT,
      },
      {
        title: this.translations.unapproveEvent,
        value: GridActions.UNAPPROVE_EVENT,
      },
    ];
  }
}
